import React, { useState } from "react";
import { Box, TextField, Button, Typography, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Link } from "@mui/material";
import { Email as EmailIcon, Person as PersonIcon, Lock as LockIcon } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink } from "react-router-dom";
import { apiFetch } from "./apiFetch";
import PreAuthShell from "./PreAuthShell";

const REGISTER_URL = "https://refinery-user-service-hvg0g8ceddefaseb.australiasoutheast-01.azurewebsites.net/register";


const RegistrationPage = () => {
  // const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    termsAccepted: false,
  });
  const [openTerms, setOpenTerms] = useState(false);
  const [termsText, setTermsText] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleOpenTerms = (e) => {
    e.preventDefault();
    setOpenTerms(true);
  };

  const handleCloseTerms = () => {
    setOpenTerms(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    if (!form.termsAccepted) {
      setErrorMsg("Please accept the terms and conditions to proceed.");
      return;
    }
    try {
      const response = await apiFetch(REGISTER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password: form.password,
          email: form.email,
          username: form.username,
        }),
      });
      console.log('Raw registration response:', response);
      if (response.ok) {
        const data = await response.json();
        console.log('Registration response data:', data);
        if (data.token) {
          localStorage.setItem("authToken", data.token);
          console.log('Token set in localStorage:', localStorage.getItem("authToken"));
          setSuccessMsg("Registration successful! Redirecting to your notes...");
          window.location.replace("/app");
        } else {
          setErrorMsg("Registration succeeded but no token was returned. Please try signing in.");
          console.log('Registration succeeded but no token:', data);
        }
      } else {
        let error;
        try {
          error = await response.json();
        } catch (e) {
          error = { message: 'Non-JSON error', raw: await response.text() };
        }
        console.error('Registration error response:', error);
        setErrorMsg(error.message || "Registration failed.");
      }
    } catch (error) {
      console.error("Error during registration:", error);
      setErrorMsg("An error occurred. Please try again later.");
    }
  };

  // Fetch terms when dialog opens
  React.useEffect(() => {
    if (openTerms && !termsText) {
      fetch("/terms_and_conditions.md")
        .then((res) => res.text())
        .then((text) => setTermsText(text))
        .catch(() => setTermsText("Failed to load terms and conditions."));
    }
  }, [openTerms, termsText]);

  return (
    <PreAuthShell
      title="Create your account"
      subtitle="Start organizing your ideas in one connected space."
      footer={
        <Typography variant="body2" align="center" sx={{ color: "#5a4a7b" }}>
          Already registered?{" "}
          <Link
            component={RouterLink}
            to="/signin"
            sx={{ color: "#492099", textDecoration: "underline", fontWeight: 600 }}
          >
            Sign in
          </Link>
        </Typography>
      }
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", mb: 1, mt: 0.5 }}>
        {errorMsg && (
          <Typography variant="body2" sx={{ color: "red", mb: 1, textAlign: "center" }}>{errorMsg}</Typography>
        )}
        {successMsg && (
          <Typography variant="body2" sx={{ color: "green", mb: 1, textAlign: "center" }}>{successMsg}</Typography>
        )}
        <TextField
          label="Email"
          name="email"
          type="email"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.email}
          onChange={handleChange}
          required
          InputProps={{
            sx: { "& input": { fontSize: 16 } },
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ color: "#8b7fa3" }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Username"
          name="username"
          type="text"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.username}
          onChange={handleChange}
          required
          InputProps={{
            sx: { "& input": { fontSize: 16 } },
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ color: "#8b7fa3" }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Password"
          name="password"
          type="password"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.password}
          onChange={handleChange}
          required
          InputProps={{
            sx: { "& input": { fontSize: 16 } },
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: "#8b7fa3" }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              name="termsAccepted"
              checked={form.termsAccepted}
              onChange={handleChange}
              color="primary"
              sx={{ mt: -1 }}
            />
          }
          label={<span style={{ fontSize: "0.92rem" }}>
            I agree to <button type="button" onClick={handleOpenTerms} style={{ color: "#492099", textDecoration: "underline", background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer" }}>terms and conditions</button>
          </span>}
          sx={{ marginTop: 1, fontSize: "0.92rem" }}
        />
        <Dialog open={openTerms} onClose={handleCloseTerms}>
          <DialogTitle>Terms and Conditions</DialogTitle>
          <DialogContent>
            <Typography component="div" variant="body2">
              <ReactMarkdown>{termsText || "Loading..."}</ReactMarkdown>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTerms}>Close</Button>
          </DialogActions>
        </Dialog>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            mb: 0.5,
            py: 1.35,
            borderRadius: 99,
            fontWeight: 600,
            textTransform: "none",
            background: "linear-gradient(135deg, #D7C4FA 0%, #8C60E6 45%, #4B1FC5 100%)",
            boxShadow: "0 10px 20px rgba(90, 50, 186, 0.3)",
            fontSize: 16,
            ":hover": {
              boxShadow: "0 12px 22px rgba(90, 50, 186, 0.35)",
            },
          }}
        >
          Register
        </Button>
      </Box>
    </PreAuthShell>
  );
};

export default RegistrationPage;
